import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { Button, CircularProgress } from '@material-ui/core';
import './Test.css';
import { useDispatch, useSelector } from 'react-redux';
import UserNav from '../../components/UserNav';
import config from '../../config';

const TestLS = () => {
	const history = useHistory();
	const dispatch = useDispatch();
	const selectedTest = useSelector(state => state.testSelected);
	const user = useSelector(state => state.auth.user);
	const [beginTest, setBeginTest] = useState(false);
	const [testQuestions, setTestQuestions] = useState([]);
	const [answers, setAnswers] = useState([]);
	const [progress, setProgress] = useState(0);
	const [testResult, setTestResult] = useState(null);
	const [page, setPage] = useState(0);
	const [attempts, setAttempts] = useState([]);
	const QUESTIONS_PER_PAGE = 5;

	const STRATEGY_DEFINITIONS = {
		apprentice: 'Mentor-Student interaction. Content is presented in a systematic step-by-step procedural way so a student can learn by following simple steps.',
		incidental: 'Case studies and real-life scenarios trigger learning experiences. Content is presented in real-life scenarios or case studies and the \'events\' in the story trigger the learning experience. These events lead to incidental learning.',
		deductive: 'Examples illustrate principles, enabling students to develop understanding. Content is presented with examples that illustrate the principle clearly to a student. The examples enable students to develop their own understanding of the concept.',
		inductive: 'Interactive assignments lead to concept understanding. Content is presented with interactive animations that allow the student to learn by doing interactive assignments that lead to the understanding of a concept.',
		discovery: 'Learning by doing and making discoveries through interactive assignments. Content is presented with interactive animations that allow the student to learn by doing interactive assignments that lead to the understanding of a concept.'
	};

	useEffect(() => {
		if (!selectedTest || !selectedTest.title) {
			const stored = localStorage.getItem('selectedTest');
			if (stored) {
				const parsed = JSON.parse(stored);
				dispatch({ type: 'SET_SELECTED_TEST', payload: parsed.title });
			} else {
				history.replace('/usertests');
			}
		}
	}, [selectedTest, dispatch, history]);

	useEffect(() => {
		if (user && user.email) {
			fetch(`${config.API_BASE}/results/${user.email}`)
				.then(res => res.json())
				.then(data => setAttempts(data))
				.catch(err => console.error('Error fetching attempts:', err));
		}
	}, [user]);

	useEffect(() => {
		if (beginTest) {
			const getTestData = async () => {
				try {
					const response = await fetch(`${config.API_BASE}/questions?type=Learning%20Strategy%20Test`);
					const questions = await response.json();
					setTestQuestions(questions);
					console.log('Fetched questions:', questions);
				} catch (error) {
					console.error('Error fetching questions:', error);
					setTestQuestions([]);
				}
			};
			getTestData();
		}
	}, [beginTest]);

	const getAttemptCount = () => {
		return attempts.filter(a => 
			a.testType === 'Learning Strategy Test' || 
			a.testType === 'learningStrategy' ||
			a.testType.toLowerCase().includes('learningstrategy')
		).length;
	};

	const getButtonText = () => {
		const count = getAttemptCount();
		if (count === 0) return 'Begin Test';
		return 'Retake Test';
	};

	const getStrategyResults = () => {
		let answerArr = [ ...answers ];
		let apprenticeArr = answerArr.slice(0, 5);
		let incidentalArr = answerArr.slice(5, 10);
		let inductiveArr = answerArr.slice(10, 15);
		let deductiveArr = answerArr.slice(15, 20);
		let discoveryArr = answerArr.slice(20);

		let answerObj = {
			apprentice: 0,
			incidental: 0,
			inductive: 0,
			deductive: 0,
			discovery: 0
		};

		apprenticeArr.forEach((el) => {
			if (el === 'yes') answerObj['apprentice']++;
		});
		incidentalArr.forEach((el) => {
			if (el === 'yes') answerObj['incidental']++;
		});
		inductiveArr.forEach((el) => {
			if (el === 'yes') answerObj['inductive']++;
		});
		deductiveArr.forEach((el) => {
			if (el === 'yes') answerObj['deductive']++;
		});
		discoveryArr.forEach((el) => {
			if (el === 'yes') answerObj['discovery']++;
		});

		const res = Object.keys(answerObj).reduce((a, b) => (answerObj[a] > answerObj[b] ? a : b));
		return res;
	};

	const insertResult = async (insertkey, insertvalue) => {
		const currentAnswers = [...answers];
		try {
			await fetch(`${config.API_BASE}/results`, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({
					email: user.email,
					key: insertkey,
					value: insertvalue,
					answers: currentAnswers
				})
			});
		} catch (error) {
			console.error('Error submitting result:', error);
		}
	};

	const totalPages = Math.ceil(testQuestions.length / QUESTIONS_PER_PAGE);
	const startIdx = page * QUESTIONS_PER_PAGE;
	const endIdx = startIdx + QUESTIONS_PER_PAGE;
	const currentQuestions = testQuestions.slice(startIdx, endIdx);

	const handleNextPage = () => {
		if (page < totalPages - 1) setPage(page + 1);
	};
	const handlePrevPage = () => {
		if (page > 0) setPage(page - 1);
	};

	if (!selectedTest || selectedTest.title !== 'Learning Strategy Test') {
		return <div className="test__container">No test selected.</div>;
	}

	return (
		<div className="test__container" style={{ position: 'relative', minHeight: '100vh', background: '#f5f8ff', paddingTop: 64 }}>
			<UserNav />
			{!beginTest && !testResult && (
				<>
					<svg className="cube-bg" width="420" height="420" viewBox="0 0 64 64" fill="none" style={{ position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%, -50%)', zIndex: 0, opacity: 0.08 }}>
						<rect x="12" y="20" width="40" height="24" rx="4" fill="#E3EAFD" stroke="#1A237E" strokeWidth="2"/>
						<path d="M12 20L32 8L52 20" stroke="#1A237E" strokeWidth="2" fill="none"/>
						<path d="M32 8V40" stroke="#1A237E" strokeWidth="2"/>
						<path d="M12 44L32 56L52 44" stroke="#1A237E" strokeWidth="2" fill="none"/>
					</svg>
					<div style={{ position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%, -50%)', zIndex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
						<h2 style={{ fontSize: 32, fontWeight: 700, marginBottom: 32 }}>{selectedTest.title}</h2>
						<Button onClick={() => setBeginTest(true)} variant="contained" color="primary" size="large" style={{ fontSize: 20, padding: '16px 48px' }}>
							{getButtonText()}
						</Button>
					</div>
				</>
			)}
			{beginTest && testQuestions.length > 0 && !testResult && (
				<div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
					<CircularProgress variant="determinate" value={progress} style={{ width: '100%', maxWidth: 500, marginBottom: 24 }} />
					<div style={{ width: '100%', maxWidth: 500 }}>
						{currentQuestions.map((q, idx) => (
							<div key={startIdx + idx} style={{ marginBottom: 24, background: '#fff', borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.06)', padding: 16 }}>
								<h3>Question {startIdx + idx + 1} of {testQuestions.length}</h3>
								<p>{q.question}</p>
								<div style={{ display: 'flex', gap: 16, marginTop: 8 }}>
									<Button
										variant={answers[startIdx + idx] === 'yes' ? 'contained' : 'outlined'}
										color="primary"
										onClick={() => {
											let arr = [...answers];
											arr[startIdx + idx] = 'yes';
											setAnswers(arr);
											setProgress(((startIdx + idx + 1) / testQuestions.length) * 100);
										}}
									>
										Yes
									</Button>
									<Button
										variant={answers[startIdx + idx] === 'no' ? 'contained' : 'outlined'}
										color="secondary"
										onClick={() => {
											let arr = [...answers];
											arr[startIdx + idx] = 'no';
											setAnswers(arr);
											setProgress(((startIdx + idx + 1) / testQuestions.length) * 100);
										}}
									>
										No
									</Button>
								</div>
							</div>
						))}
					</div>
					<div style={{ display: 'flex', gap: 16, marginTop: 32 }}>
						<Button onClick={handlePrevPage} disabled={page === 0} variant="outlined">Previous</Button>
						{page < totalPages - 1 ? (
							<Button onClick={handleNextPage} variant="contained" color="primary">Next</Button>
						) : (
							<Button
								variant="contained"
								color="primary"
								onClick={() => {
									// Only allow finish if all questions are answered
									if (answers.length < testQuestions.length || answers.includes(undefined)) {
										alert('Please answer all questions before finishing.');
										return;
									}
									let res = getStrategyResults();
									let key = 'learningStrategy';
									setTestResult(res);
									setBeginTest(false);
									insertResult(key, res);
								}}
							>
								Finish
							</Button>
						)}
					</div>
				</div>
			)}
			{testResult && (
				<div className="test__complete" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
					<div style={{ fontSize: 22, fontWeight: 500, marginBottom: 16 }}>Test complete!</div>
					<strong style={{ fontSize: 24, marginBottom: 16 }}>Your learning strategy is: {testResult.charAt(0).toUpperCase() + testResult.slice(1)}</strong>
					<div style={{ maxWidth: 600, textAlign: 'center', fontSize: 18, marginTop: 16, background: '#f5f8ff', borderRadius: 8, padding: 20, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
						{STRATEGY_DEFINITIONS[testResult]}
					</div>
				</div>
			)}
		</div>
	);
}

export default TestLS; 